import { success } from '@/helpers/result-helper'
import { VuexResponse } from '@helpers/vuex-action-builder'
import objectSearch from '@/utils/object-search'
import dayjs from '@/services/date/index'
import { isCacheFresh } from '@/helpers/cache-helpers'
import { generateUniqueIdForNodesAtTargetLevelByParentIds } from '@/helpers/org-tree-helpers'
import { OrganisationLevel } from '@/constants/admin/OrganisationLevel'

const getDefaultState = () => {
  return {
    loadingCount: 0,
    divisionTree: [],
    organisationTree: {
      list: [],
      lastUpdated: null,
    },
    managerTree: [],
    locationTree: [],
    adminTree: [],
    loadedTree: false,
    createdTrees: {},
    staff: [],
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,

  getters: {
    isLoading: (state) => state.loadingCount > 0,
    isLoaded: (state) => state.loadedTree,
    divTree: (state) => state.divisionTree,
    organisationTree: (state) => state.organisationTree?.list || [],
    getStaff: (state) => state.staff ?? [],
    getAdminById: (state) => (adminId) =>
      state.staff.find((a) => adminId === a.id),
    getAdminNameById: (state) => (adminId) =>
      state.staff.find((a) => adminId === a.id)?.name || '',
    getManagerTree: (state) => state.managerTree ?? [],
    getFlatAdminTree: (state) => state.adminTree ?? [],
    getStaffByIds: (state) => (adminIds) =>
      state.staff.filter((a) => adminIds.includes(a.id)),
  },

  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    SET_PROMISE(state, promise) {
      state.startedPromise = promise
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
    UPDATE_STAFF(state, data) {
      state.staff = data
    },
    UPDATE_DIV_TREE(state, data) {
      state.divisionTree = data
      state.loadedTree = true
    },
    UPDATE_ORG_TREE(state, data) {
      // Generate unique ids for all admins so that they can be processed correctly
      // by the v-treeview component
      const processedTree = generateUniqueIdForNodesAtTargetLevelByParentIds(
        data,
        OrganisationLevel.Admin
      )

      state.organisationTree.list = processedTree
      state.organisationTree.lastUpdated = dayjs()
    },
    UPDATE_MANAGER_TREE(state, data) {
      state.managerTree = data
    },
    UPDATE_LOCATIONS_TREE(state, data) {
      state.locationTree = data
    },
    UPDATE_ADMIN_TREE(state, data) {
      state.adminTree = data
    },
  },

  actions: {
    async initialiseStaff({ dispatch }) {
      if (state.staff.length) return
      await dispatch('loadAllAdminDetails')
    },

    async returnDivisionTreeItemByKey({ commit, getters, dispatch }, key) {
      if (getters.isLoaded) {
        return success(objectSearch(getters.divTree, key, 'key', 'children'))
      } else {
        await dispatch('loadDivisionTree')
        return success(objectSearch(getters.divTree, key, 'key', 'children'))
      }
    },

    async loadDivisionTree({ getters, commit, dispatch }) {
      if (getters.isLoaded) {
        return success(getters.divTree)
      }

      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.staff.loadDivisionTree())
        .withLoading()
        .withCommit('UPDATE_DIV_TREE')
        .withFailureToast('Failed to load the division tree')
        .go()
    },

    async loadOrganisationTree(
      { getters, commit, dispatch, state },
      forceRefresh = false
    ) {
      if (
        state.organisationTree.list &&
        state.organisationTree.list.length > 0 &&
        isCacheFresh({
          cacheDuration: 1,
          duractionUnits: 'hours',
          lastUpdated: state.organisationTree.lastUpdated,
          forceRefresh,
        })
      )
        return Promise.resolve(success(getters.organisationTree))

      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.staff.loadOrganisationTree())
        .withLoading()
        .withCommit('UPDATE_ORG_TREE')
        .withFailureToast('Failed to load the organisation tree')
        .go()
    },

    async loadFlatAdminTree({ commit, dispatch }) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.staff.loadManagerTree())
        .withLoading()
        .withCommit('UPDATE_ADMIN_TREE')
        .go()
    },

    async loadManagerAdminTree({ getters, commit }) {
      const sortedStaff = getters.getStaff
        .map((s) => s)
        .sort((a, b) => a.id - b.id)

      const createNode = (nodes, parent) => {
        for (const node of nodes) {
          if (node.id === parent.reportsTo) {
            node.children.push({
              id: parent.id,
              title: parent.name,
              children: [],
              key: `${parent.id}-Admin`,
            })
          } else {
            createNode(node.children, parent)
          }
        }
      }

      const tree = []
      for (const staff of sortedStaff) {
        if (!staff.reportsTo) {
          tree.push({
            id: staff.id,
            title: staff.name,
            children: [],
            key: `${staff.id}-Admin`,
          })
        } else {
          createNode(tree, staff)
        }
      }
      commit('UPDATE_MANAGER_TREE', tree)
      success(tree)
    },

    async loadAdminClientTree({ commit, dispatch }, adminIds) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.staff.loadAdminClientLocationTree(adminIds))
        .withLoading()
        .withCommit('UPDATE_LOCATIONS_TREE')
        .go()
    },

    async loadAllAdminDetails({ commit, dispatch }) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.staff.loadAllAdmins())
        .withLoading()
        .withCommit('UPDATE_STAFF')
        .go()
    },

    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
